import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Card,
  Badge,
  Dropdown,
  Form,
  Alert,
} from "react-bootstrap";
import { Users, DollarSign, X, Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllMaids,
  verifyBulkTeams,
} from "../../../../redux/Actions/maidAction";
import { message } from "antd";
import { VERIFY_BULK_TEAMS_RESET } from "../../../../redux/Constants/maidConstants";

export default function BulkVerificationModal({
  show,
  onHide,
  teams = [],
  service,
}) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin);
  const { maids } = useSelector((state) => state.maidsList);

  const {
    loading: bulkLoading,
    error: bulkError,
    success: bulkSuccess,
    failedTransfers,
    message: successMessage,
    payouts,
  } = useSelector((state) => state.verifyBulkTeams);

  const valets = maids?.filter((m) => m.type === service);

  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (show && teams.length > 0) {
      dispatch(getAllMaids(teams[0].hotel, service));
      setMembers([]);
      setError("");
      dispatch({ type: VERIFY_BULK_TEAMS_RESET });
    }
  }, [show]);

  const totalAmount = teams.reduce((sum, t) => sum + (t.tip || 0), 0);

  const handleAdd = (v) => {
    if (members.find((m) => m._id === v._id)) return;

    setMembers((prev) => [...prev, v]);

    if (error) setError("");
  };

  const handleRemove = (id) => {
    setMembers(members.filter((m) => m._id !== id));
  };
  const handleVerify = () => {
    setError("");

    if (members.length === 0) {
      setError("Please select at least one team member.");
      return;
    }

    const hasInactive =
      !userInfo?.model?.b2b && members.some((m) => !m.chargesStatus);

    if (hasInactive) {
      const inactiveMembers = members
        .filter((m) => !m.chargesStatus)
        .map((m) => m.serviceName)
        .join(", ");

      setError(`Inactive members cannot receive payouts: ${inactiveMembers}`);
      return;
    }

    const payload = {
      hotelId: teams[0]?.hotel,
      teamMembers: members,
      poolTips: teams,
    };

    dispatch(verifyBulkTeams(payload));
  };

  useEffect(() => {
    if (bulkSuccess) {
      onHide();
      dispatch({ type: VERIFY_BULK_TEAMS_RESET });
    }
  }, [bulkSuccess]);

  useEffect(() => {
    if (!bulkSuccess) return;

    //  FULL SUCCESS
    if (!failedTransfers || failedTransfers.length === 0) {
      message.success(`${teams.length} tips verified successfully`);

      onHide();
    }

    //  PARTIAL SUCCESS
    else {
      message.warning(
        `${payouts?.length || 0} payouts succeeded, ${
          failedTransfers.length
        } failed`,
      );
    }

    dispatch({ type: VERIFY_BULK_TEAMS_RESET });
  }, [bulkSuccess]);

  return (
    <Modal
      show={show}
      centered
      size="lg"
      onHide={!bulkLoading ? onHide : undefined}
      backdrop={bulkLoading ? "static" : true}
      keyboard={!bulkLoading}
    >
      <Modal.Header className="bg-primary text-white">
        <Modal.Title>Bulk Verification</Modal.Title>
      </Modal.Header>

      {bulkLoading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            background: "rgba(0,0,0,0.4)",
            zIndex: 10,
            borderRadius: "8px",
          }}
        >
          <div className="text-center text-white">
            <div className="spinner-border text-success mb-2" />
            <div>Processing verification...</div>
          </div>
        </div>
      )}

      <Modal.Body className="position-relative">
        {(error || bulkError) && (
          <Alert variant="danger" className="mb-3">
            {error || bulkError}
          </Alert>
        )}

        {/* Tip total */}
        <Card className="mb-3 p-2 text-center border-success">
          <h5>${totalAmount.toFixed(2)}</h5>
          <small>{teams.length} tips selected</small>
        </Card>

        {/* Members */}
        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between">
            <span>Team Members ({members.length})</span>

            <Dropdown>
              <Dropdown.Toggle size="sm" variant="outline-primary">
                <Plus size={14} /> Add
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Form.Control
                  placeholder="Search..."
                  className="mb-2"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                {valets
                  ?.filter((v) =>
                    v.serviceName.toLowerCase().includes(search.toLowerCase()),
                  )
                  .map((v) => (
                    <Dropdown.Item
                      key={v._id}
                      onClick={() => !bulkLoading && handleAdd(v)}
                    >
                      {v.serviceName}
                    </Dropdown.Item>
                  ))}
              </Dropdown.Menu>
            </Dropdown>
          </Card.Header>

          <Card.Body>
            <div className="d-flex flex-wrap gap-2">
              {members.map((m) => (
                <Badge
                  key={m._id}
                  bg="primary"
                  className="d-flex align-items-center gap-2 px-2 py-1"
                >
                  {/* Status dot */}
                  {!userInfo?.model?.b2b && (
                    <span
                      className={`rounded-circle ${
                        m.chargesStatus ? "bg-success" : "bg-danger"
                      }`}
                      style={{
                        width: "8px",
                        height: "8px",
                      }}
                    />
                  )}

                  <span>{m.serviceName}</span>

                  <Button
                    size="sm"
                    variant="link"
                    className="text-white p-0 ms-1"
                    onClick={() => !bulkLoading && handleRemove(m._id)}
                  >
                    <X size={12} />
                  </Button>
                </Badge>
              ))}
            </div>
          </Card.Body>
        </Card>

        {/* Pool tips preview */}
        <div className="small text-muted">
          {teams.map((t) => (
            <div key={t._id}>
              • {t.location || "Not Defined"} → ${t.tip}
            </div>
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          disabled={bulkLoading}
          onClick={onHide}
        >
          Cancel
        </Button>

        <Button variant="success" onClick={handleVerify} disabled={bulkLoading}>
          {bulkLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Verifying...
            </>
          ) : (
            "Verify All"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
